import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, ArrowUpRight } from 'lucide-react';
import { formatBlogDate } from '../services/blogService';
import './BlogCard.css';

// Inline SVG placeholder (no network request needed)
const PLACEHOLDER_SVG = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect fill='%23e8f5fd' width='400' height='300'/%3E%3Ctext fill='%2360C5FE' font-family='Arial' font-size='14' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3ELoading...%3C/text%3E%3C/svg%3E`;

/**
 * Reusable Blog Card Component
 * Displays a blog post preview with title, excerpt, date, and featured image
 * Links to the original blog post on GrowLeads.io
 */
const BlogCard = ({ 
  title, 
  excerpt, 
  pubDate, 
  featuredImage, 
  link,
  categories = [],
  author = '',
  index = 0,
  variant = 'default' // 'default' | 'compact'
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageSrc, setImageSrc] = useState(PLACEHOLDER_SVG);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);
  
  const formattedDate = formatBlogDate(pubDate);
  
  // Lazy load image when in viewport
  useEffect(() => {
    if (!featuredImage) {
      setImageLoaded(true);
      return;
    }
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Start loading the actual image
            const img = new Image();
            img.onload = () => {
              setImageSrc(featuredImage);
              setImageLoaded(true);
            };
            img.onerror = () => {
              setHasError(true);
              setImageLoaded(true);
            };
            img.src = featuredImage;
            observer.disconnect();
          }
        });
      },
      { rootMargin: '100px' } // Load 100px before entering viewport
    );
    
    if (imgRef.current) {
      observer.observe(imgRef.current);
    }
    
    return () => observer.disconnect();
  }, [featuredImage]);
  
  return (
    <motion.article
      className={`blog-card blog-card--${variant}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -4, transition: { duration: 0.2 } }}
    >
      <a
        href={link}
        {...(link && link.startsWith('/') ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
        className="blog-card__link"
        aria-label={`Read "${title}"`}
      >
        {/* Featured Image */}
        <div className="blog-card__image-wrapper" ref={imgRef}>
          {!imageLoaded && (
            <div className="blog-card__skeleton" />
          )}
          <img 
            src={imageSrc} 
            alt={title}
            className={`blog-card__image ${imageLoaded ? 'blog-card__image--loaded' : ''}`}
          />
          <div className="blog-card__image-overlay">
            <span className="blog-card__read-label">
              {link && link.startsWith('/') ? 'Read article' : 'Read on growleads.io'}
              <ArrowUpRight size={16} />
            </span>
          </div>
        </div>
        
        {/* Content */}
        <div className="blog-card__content">
          {/* Title */}
          <h3 className="blog-card__title">{title}</h3>
          
          {/* Excerpt */}
          <p className="blog-card__excerpt">{excerpt}</p>
          
          {/* Meta */}
          <div className="blog-card__meta">
            <div className="blog-card__meta-left">
              <span className="blog-card__date">
                <Calendar size={14} />
                {formattedDate}
              </span>
              {author && (
                <span className="blog-card__author">{author}</span>
              )}
            </div>
            <span className="blog-card__cta">
              Read More
              <ArrowUpRight size={14} />
            </span>
          </div>
        </div>
      </a>
    </motion.article>
  );
};

export default BlogCard;
